import React, { useState, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Upload, Trash2, Eye, Undo2, FileText, X } from 'lucide-react';
import { formatBytes } from '../../context/DownloadContext';

/**
 * Shared Division 7 wizard submittals step.
 * Works with useSubmittalsManager hook — all changes are staged, not persisted.
 *
 * Props:
 *  - displayList      — from useSubmittalsManager
 *  - onAddUpload(file, docType)
 *  - onRemoveItem(id)
 *  - onUndoDelete(id)
 *  - onPreview(submittal) — only for committed items
 *  - docTypes          — string[] of available document types
 */
export const WizardSubmittalsStep = ({
  displayList,
  onAddUpload,
  onRemoveItem,
  onUndoDelete,
  onPreview,
  docTypes,
}) => {
  const [docType, setDocType] = useState('');
  const fileRef = useRef(null);

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (f && docType) {
      const isPdf = f.name.toLowerCase().endsWith('.pdf') || f.type === 'application/pdf';
      if (!isPdf) {
        e.target.value = '';
        return;
      }
      onAddUpload(f, docType);
      setDocType('');
    }
    e.target.value = '';
  };

  const committed = displayList.filter(s => s._status === 'committed');
  const pendingDeletes = displayList.filter(s => s._status === 'pendingDelete');
  const pendingUploads = displayList.filter(s => s._status === 'pending');

  return (
    <div className="space-y-4" data-testid="wizard-submittals-step">
      <h4 className="font-semibold text-sm border-b pb-2">Submittals</h4>

      {/* Upload control */}
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <Label className="text-sm mb-1 block">
            Document Type <span className="text-xs text-slate-400 font-normal ml-1">PDF only</span>
          </Label>
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger data-testid="submittal-doc-type-select">
              <SelectValue placeholder="Select document type..." />
            </SelectTrigger>
            <SelectContent>
              {docTypes.map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={!docType}
          onClick={() => fileRef.current?.click()}
          data-testid="upload-submittal-btn"
        >
          <Upload className="h-4 w-4 mr-1.5" />Upload
        </Button>
        <input
          type="file"
          accept=".pdf"
          className="hidden"
          ref={fileRef}
          onChange={handleFileSelect}
        />
      </div>

      {/* Table */}
      {displayList.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="border-b">
                <th className="px-3 py-2 text-left font-medium text-slate-600">Document Type</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600">File Name</th>
                <th className="px-3 py-2 text-right font-medium text-slate-600 w-20">Size</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600 w-20">Status</th>
                <th className="px-3 py-2 text-right font-medium text-slate-600 w-28">Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* Committed (active) */}
              {committed.map(s => (
                <tr key={s.id} className="border-b" data-testid={`submittal-row-${s.id}`}>
                  <td className="px-3 py-2 text-slate-700">{s.documentType}</td>
                  <td className="px-3 py-2 text-slate-600">{s.fileName}</td>
                  <td className="px-3 py-2 text-right text-slate-500">{formatBytes(s.bytes)}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">Saved</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {onPreview && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors" onClick={() => onPreview(s)} data-testid={`preview-sub-${s.id}`} title="Preview">
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors" onClick={() => onRemoveItem(s.id)} data-testid={`delete-sub-${s.id}`} title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}

              {/* Pending uploads */}
              {pendingUploads.map(s => (
                <tr key={s.id} className="border-b bg-amber-50/40" data-testid={`submittal-row-${s.id}`}>
                  <td className="px-3 py-2 text-slate-700">{s.documentType}</td>
                  <td className="px-3 py-2 text-slate-600">{s.fileName}</td>
                  <td className="px-3 py-2 text-right text-slate-500">{formatBytes(s.bytes)}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">Pending</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-300 cursor-not-allowed" disabled title="Save first to preview">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:bg-orange-500 hover:text-white transition-colors" onClick={() => onRemoveItem(s.id)} data-testid={`remove-pending-${s.id}`} title="Remove">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}

              {/* Pending deletes (marked for removal) */}
              {pendingDeletes.map(s => (
                <tr key={s.id} className="border-b bg-red-50/40 opacity-60" data-testid={`submittal-row-del-${s.id}`}>
                  <td className="px-3 py-2 text-slate-400 line-through">{s.documentType}</td>
                  <td className="px-3 py-2 text-slate-400 line-through">{s.fileName}</td>
                  <td className="px-3 py-2 text-right text-slate-400">{formatBytes(s.bytes)}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-600">Will delete</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-slate-500 hover:bg-slate-100" onClick={() => onUndoDelete(s.id)} data-testid={`undo-delete-${s.id}`} title="Undo delete">
                      <Undo2 className="h-3.5 w-3.5 mr-1" />Undo
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/**
 * Shared Division 7 wizard submittals review section.
 */
export const WizardSubmittalsReview = ({ displayList, docTypes }) => {
  const active = displayList.filter(s => s._status !== 'pendingDelete');
  const pendingDeletes = displayList.filter(s => s._status === 'pendingDelete');

  return (
    <div className="pt-3">
      <h4 className="text-sm font-semibold text-slate-700 mb-2">
        Submittals ({active.length}/{docTypes.length})
      </h4>
      {active.length > 0 ? (
        <div className="space-y-1">
          {active.map(s => (
            <div key={s.id} className="flex items-center gap-2 text-sm">
              <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <span className="w-36 text-slate-500">{s.documentType}</span>
              <span className="flex-1 text-slate-700">{s.fileName}</span>
              <span className="text-slate-400">{formatBytes(s.bytes)}</span>
              {s._status === 'pending' && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">Pending</span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-400">No submittals uploaded</p>
      )}
      {docTypes.filter(dt => !active.some(s => s.documentType === dt)).length > 0 && (
        <div className="mt-2 space-y-0.5">
          {docTypes.filter(dt => !active.some(s => s.documentType === dt)).map(dt => (
            <div key={dt} className="flex items-center gap-2 text-sm">
              <X className="h-3.5 w-3.5 text-red-500 shrink-0" />
              <span className="text-slate-400">{dt}</span>
            </div>
          ))}
        </div>
      )}
      {pendingDeletes.length > 0 && (
        <div className="mt-2 pt-1 border-t">
          <p className="text-xs text-red-500 mb-1">Will be deleted on save:</p>
          {pendingDeletes.map(s => (
            <div key={s.id} className="flex items-center gap-2 text-sm text-red-400 line-through">
              <FileText className="h-3.5 w-3.5 shrink-0" />
              <span>{s.documentType} — {s.fileName}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WizardSubmittalsStep;
